"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { appConfig, resolveCookieDomain } from "../config/get-config";
import en from "../locales/en.json";
import ta from "../locales/ta.json";

type Messages = Record<string, string>;
type Locale = "en" | "ta";

const messages: Record<Locale, Messages> = { en, ta } as const;

function buildLanguageCookie(value: Locale) {
  const cookieDomain = resolveCookieDomain(appConfig, typeof window !== "undefined" ? window.location.hostname : undefined);
  const attributes = [
    `lang=${encodeURIComponent(value)}`,
    "path=/",
    "max-age=31536000",
    "samesite=lax",
  ];

  if (cookieDomain) {
    attributes.push(`domain=${cookieDomain}`);
  }

  return attributes.join("; ");
}

function getBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return "en";

  const candidates = [...(navigator.languages ?? []), navigator.language];

  for (const candidate of candidates) {
    const lower = candidate?.toLowerCase();
    if (!lower) continue;
    if (lower.startsWith("ta")) return "ta";
    if (lower.startsWith("en")) return "en";
  }

  return "en";
}

function getStoredLocale(): Locale | null {
  try {
    const stored = typeof window !== "undefined" ? localStorage.getItem("lang") : null;
    return stored === "en" || stored === "ta" ? stored : null;
  } catch {
    return null;
  }
}

function getPreferredLocale(): Locale {
  return getStoredLocale() ?? getBrowserLocale();
}

type I18nContextType = {
  lang: Locale;
  t: (key: string) => string;
  setLanguage: (lang: Locale) => void;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({
  children,
  initialLang,
}: {
  children: React.ReactNode;
  initialLang?: Locale;
}) {
  const [lang, setLang] = useState<Locale>(initialLang ?? "en");

  useEffect(() => {
    const preferredLocale = getPreferredLocale();
    if (preferredLocale === lang) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setLang((current) => (current === preferredLocale ? current : preferredLocale));
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [lang]);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      // ignore
    }
  }, [lang]);

  const setLanguage = (l: Locale) => {
    setLang(l);
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("lang", l);
        document.cookie = buildLanguageCookie(l);
        document.documentElement.lang = l;
      }
    } catch {
      // ignore
    }
  };

  const t = useMemo(() => {
    const current = messages[lang] || messages.en;
    const fallback = messages.en;
    return (key: string) => current[key] ?? fallback[key] ?? key;
  }, [lang]);

  const value: I18nContextType = useMemo(
    () => ({ lang, t, setLanguage }),
    [lang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
